import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Instagram, LogOut, UserCircle2, Globe, LogIn, ChevronDown, Megaphone, Users, Vote, Mic, Download, Map, BookOpen, Scale } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import BrandLogo, { CefLogoImg } from "@/components/site/BrandLogo";
import SearchPalette from "@/components/site/SearchPalette";
import NavDropdown from "@/components/site/NavDropdown";
import { CEF_LINKS, CEF_HANDLES } from "@/lib/cef-links";
import FloatingHub from "@/components/site/FloatingHub";

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.27a8.16 8.16 0 0 0 4.77 1.52V7.4a4.85 4.85 0 0 1-1.84-.71z" /></svg>
);
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.84 9.84 0 0 0 12.04 2Zm5.83 14.04c-.25.7-1.43 1.33-2.01 1.42-.51.07-1.18.11-1.9-.12-.44-.14-1-.32-1.72-.63-3.03-1.31-5-4.36-5.16-4.56-.15-.2-1.22-1.62-1.22-3.09 0-1.47.77-2.19 1.04-2.49.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.07.92 2.22.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.18-.32.4-.45.54-.15.15-.31.31-.13.62.18.32.81 1.33 1.74 2.16 1.2 1.07 2.21 1.4 2.52 1.55.32.15.51.13.7-.08.2-.22.81-.94 1.03-1.27.22-.32.45-.27.74-.16.3.11 1.88.89 2.2 1.05.32.16.54.24.62.37.07.13.07.78-.18 1.48Z"/></svg>
);

// Sub-items mirrored from /agir and /comprendre. Keep in sync if those pages change.
const AGIR_ITEMS = [
  { to: "/carte", label: "Créer ou rejoindre une action", desc: "Carte interactive — rassemblements, marches, blocages…", Icon: Megaphone, color: "bg-cef-pink text-white", testid: "agir-carte" },
  { to: "/carte#groupes", label: "Rejoindre un groupe local", desc: "Trouve l'antenne CEF près de chez toi.", Icon: Users, color: "bg-cef-green", testid: "agir-groupes" },
  { to: "/donner-mon-avis", label: "Donner mon avis", desc: "Vote, sondages, boîte à idées.", Icon: Vote, color: "bg-cef-yellow", testid: "agir-avis" },
  { to: "/temoignages", label: "Témoigner", desc: "Partage ton vécu, anonymement ou non.", Icon: Mic, color: "bg-cef-blue text-white", testid: "agir-temoignages" },
  { to: "/actions", label: "Pack Action", desc: "Kits, guides PDF, ressources mobilisation.", Icon: Download, color: "bg-white", testid: "agir-pack" },
  { to: "/rejoindre", label: "Devenir membre du CEF", desc: "Rejoins l'équipe officielle.", Icon: Users, color: "bg-black text-white", testid: "agir-rejoindre" },
  { to: "/carte", label: "La carte des actions", desc: "Voir toutes les mobilisations en cours.", Icon: Map, color: "bg-cef-green", testid: "agir-carte-detail" },
];

const COMPRENDRE_ITEMS = [
  { to: "/comprendre?tab=reformes", label: "Décoder les réformes", desc: "Réformes, analyses et infographies.", Icon: BookOpen, color: "bg-cef-blue text-white", testid: "comprendre-reformes" },
  { to: "/comprendre?tab=droits", label: "Connaître tes droits", desc: "FAQ vérifiée sur tes droits d'élève.", Icon: Scale, color: "bg-cef-yellow", testid: "comprendre-droits" },
];

export default function Layout({ children }) {
  const [open, setOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [mobileSection, setMobileSection] = React.useState(null); // "agir" | "comprendre" | null
  const location = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();

  React.useEffect(() => { setOpen(false); setMenuOpen(false); setMobileSection(null); }, [location.pathname, location.search]);

  const FOURTH = user
    ? { to: "/profil", label: "Profil", testid: "profil" }
    : { to: "/rejoindre", label: "Rejoindre le mouvement", testid: "rejoindre" };

  const UserBadge = user ? (
    <div className="relative">
      <button onClick={() => setMenuOpen((o) => !o)} className="neo-btn bg-cef-yellow text-sm py-2 px-3" data-testid="user-menu-toggle">
        <UserCircle2 className="w-4 h-4" /> <span className="hidden sm:inline max-w-[100px] truncate">{user.name}</span>
      </button>
      {menuOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white border-2 border-black shadow-brutal rounded-md z-50" data-testid="user-menu-dropdown">
          <Link to="/profil" className="block px-4 py-3 hover:bg-cef-yellow font-bold text-sm border-b-2 border-black" data-testid="user-menu-profil">Espace membre</Link>
          <Link to="/cef" className="block px-4 py-3 hover:bg-cef-yellow font-bold text-sm border-b-2 border-black" data-testid="user-menu-cef">À propos du CEF</Link>
          <button onClick={() => { logout(); nav("/"); }} className="w-full text-left px-4 py-3 hover:bg-cef-pink hover:text-white font-bold text-sm" data-testid="user-menu-logout"><LogOut className="w-3.5 h-3.5 inline mr-1" /> Déconnexion</button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="min-h-screen flex flex-col bg-cef-paper">
      <header className="sticky top-0 z-40 bg-white border-b-2 border-black" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-3 sm:gap-4 h-16">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group shrink-0" data-testid="logo-link">
            <BrandLogo size="md" />
          </Link>
          {/* CEF logo — separate link to lecef.org (next to title, vertically centered) */}
          <a
            href={CEF_LINKS.website}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex shrink-0 border border-black/30 rounded-sm bg-white p-0.5 shadow-[1px_1px_0_rgba(0,0,0,1)] hover:scale-105 transition"
            aria-label="Site officiel du Comité des Élèves Francophones (lecef.org)"
            data-testid="header-cef-link"
          >
            <CefLogoImg className="h-7 sm:h-9 w-auto" />
          </a>

          <div className="flex-1" />

          <nav className="hidden lg:flex items-center gap-1 ml-auto" data-testid="desktop-nav">
            <NavLink to="/actualites" className={({ isActive }) => `px-3 py-2 text-sm font-bold rounded-md border-2 transition whitespace-nowrap ${isActive ? "bg-cef-blue text-white border-black shadow-brutal" : "border-transparent hover:border-black hover:bg-cef-yellow"}`} data-testid="nav-actualites">Actualités</NavLink>
            <NavDropdown label="Comprendre" testId="comprendre" items={COMPRENDRE_ITEMS} parentMatchPrefixes={["/comprendre", "/droits"]} />
            <NavDropdown label="Agir" testId="agir" items={AGIR_ITEMS} parentMatchPrefixes={["/agir", "/carte", "/actions", "/temoignages", "/donner-mon-avis"]} />
            <NavLink to={FOURTH.to} className={({ isActive }) => `px-3 py-2 text-sm font-bold rounded-md border-2 transition whitespace-nowrap ${isActive ? "bg-cef-blue text-white border-black shadow-brutal" : FOURTH.to === "/rejoindre" ? "bg-cef-pink text-white border-black shadow-brutal hover:bg-cef-pink" : "border-transparent hover:border-black hover:bg-cef-yellow"}`} data-testid={`nav-${FOURTH.testid}`}>{FOURTH.label}</NavLink>
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <SearchPalette />
            {!user && (
              <Link to="/login" className="hidden sm:inline-flex items-center gap-1.5 neo-btn bg-white text-sm py-2 px-3" data-testid="header-login-link">
                <LogIn className="w-4 h-4" /> <span className="hidden md:inline">Se connecter</span>
              </Link>
            )}
            {UserBadge}
            <button className="lg:hidden neo-btn bg-white text-black p-2" onClick={() => setOpen((o) => !o)} aria-label="Menu" data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t-2 border-black bg-white" data-testid="mobile-nav">
            <div className="px-4 py-3 grid gap-2">
              <NavLink to="/actualites" className={({ isActive }) => `px-3 py-2 text-sm font-bold rounded-md border-2 border-black ${isActive ? "bg-cef-blue text-white shadow-brutal" : "bg-white"}`} data-testid="mobile-nav-actualites">Actualités</NavLink>

              <MobileSection label="Comprendre" items={COMPRENDRE_ITEMS} openId="comprendre" current={mobileSection} setCurrent={setMobileSection} />
              <MobileSection label="Agir" items={AGIR_ITEMS} openId="agir" current={mobileSection} setCurrent={setMobileSection} />

              <NavLink to={FOURTH.to} className={({ isActive }) => `px-3 py-2 text-sm font-bold rounded-md border-2 border-black ${isActive ? "bg-cef-blue text-white shadow-brutal" : FOURTH.to === "/rejoindre" ? "bg-cef-pink text-white" : "bg-white"}`} data-testid={`mobile-nav-${FOURTH.testid}`}>{FOURTH.label}</NavLink>

              {!user && (
                <>
                  <Link to="/login" className="neo-btn bg-cef-yellow inline-flex items-center justify-center gap-1.5" data-testid="mobile-login"><LogIn className="w-4 h-4" /> Se connecter</Link>
                  <Link to="/signup" className="neo-btn bg-white" data-testid="mobile-signup">Créer un compte</Link>
                </>
              )}
              <a href={CEF_LINKS.website} target="_blank" rel="noreferrer" className="px-3 py-2 text-sm font-bold rounded-md border-2 border-black bg-white inline-flex items-center gap-2" data-testid="mobile-cef-link">
                <CefLogoImg className="h-5 w-auto" /> Site du CEF — lecef.org
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1" data-testid="main-content">{children}</main>
      <FloatingHub />

      <footer className="bg-black text-white mt-16" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid gap-10 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <a href={CEF_LINKS.website} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center w-12 h-12 bg-white border-2 border-white rounded-md shadow-brutal overflow-hidden hover:scale-105 transition" aria-label="lecef.org" data-testid="footer-cef-link">
                <CefLogoImg className="w-10 h-10" />
              </a>
              <div>
                <div className="font-display font-black text-xl tracking-tighter">ÉLÈVE TA VOIX<span className="text-cef-pink">.</span></div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/60">by le Comité des Élèves Francophones</div>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">La plateforme des élèves du secondaire en Fédération Wallonie-Bruxelles. Faite par et pour les élèves.</p>
          </div>
          <div>
            <div className="font-bold uppercase text-xs tracking-widest mb-3 text-cef-yellow">Explorer</div>
            <ul className="space-y-1 text-sm">
              <li><Link to="/actualites" className="hover:text-cef-yellow">Actualités</Link></li>
              <li><Link to="/donner-mon-avis" className="hover:text-cef-yellow">Donner son avis</Link></li>
              <li><Link to="/carte" className="hover:text-cef-yellow">Carte des initiatives</Link></li>
              <li><Link to="/droits" className="hover:text-cef-yellow">Mes droits</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-bold uppercase text-xs tracking-widest mb-3 text-cef-yellow">Le CEF</div>
            <ul className="space-y-1 text-sm">
              <li><Link to="/cef" className="hover:text-cef-yellow">Qui sommes-nous</Link></li>
              <li><Link to="/rejoindre" className="hover:text-cef-yellow">Rejoindre</Link></li>
              <li><Link to="/signup" className="hover:text-cef-yellow">Créer un compte</Link></li>
              <li><Link to="/admin/login" className="hover:text-cef-yellow">Espace admin</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-bold uppercase text-xs tracking-widest mb-3 text-cef-yellow">Suis-nous</div>
            <div className="flex gap-2 flex-wrap">
              <a href={CEF_LINKS.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center border-2 border-white hover:bg-cef-yellow hover:text-black rounded-md transition" data-testid="social-instagram" aria-label={`Instagram ${CEF_HANDLES.instagram}`}><Instagram className="w-5 h-5" /></a>
              <a href={CEF_LINKS.tiktok} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center border-2 border-white hover:bg-cef-yellow hover:text-black rounded-md transition" data-testid="social-tiktok" aria-label={`TikTok ${CEF_HANDLES.tiktok}`}><TikTokIcon className="w-5 h-5" /></a>
              {user && (
                <a href={CEF_LINKS.whatsapp_channel} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center border-2 border-white hover:bg-cef-green hover:text-black rounded-md transition" data-testid="social-whatsapp" aria-label="Canal WhatsApp CEF"><WhatsAppIcon className="w-5 h-5" /></a>
              )}
              <a href={CEF_LINKS.website} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center border-2 border-white hover:bg-cef-yellow hover:text-black rounded-md transition" data-testid="social-website" aria-label="Site web CEF"><Globe className="w-5 h-5" /></a>
            </div>
            <div className="mt-3 text-xs text-white/60">
              <a href={CEF_LINKS.website} target="_blank" rel="noreferrer" className="hover:text-cef-yellow">lecef.org →</a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/20 py-4 text-center text-xs text-white/50">© {new Date().getFullYear()} Élève ta voix · <a href="https://elevetavoix.be" className="hover:text-cef-yellow font-bold">elevetavoix.be</a> · Une plateforme du Comité des Élèves Francophones · Fédération Wallonie-Bruxelles</div>
      </footer>
    </div>
  );
}

function MobileSection({ label, items, openId, current, setCurrent }) {
  const isOpen = current === openId;
  return (
    <div className="border-2 border-black rounded-md bg-white overflow-hidden" data-testid={`mobile-section-${openId}`}>
      <button
        type="button"
        onClick={() => setCurrent(isOpen ? null : openId)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-bold"
        data-testid={`mobile-section-toggle-${openId}`}
        aria-expanded={isOpen}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {isOpen && (
        <ul className="border-t-2 border-black/10">
          {items.map((it) => (
            <li key={it.testid} className="border-b-2 border-black/10 last:border-b-0">
              <NavLink to={it.to} className="block px-4 py-3 text-sm font-bold hover:bg-cef-yellow" data-testid={`mobile-${openId}-${it.testid}`}>{it.label}</NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
